import React from "react";
import { Box } from "@mui/material";
import { useQueryClient } from "react-query";

import { ANLoadingScreen } from "@answersnow/pkg-theme";
import { useGlobalToast } from "components/GlobalToastProvider";
import { useUpdateTeamUser } from "hooks/api/useUpdateTeamUser";
import { useUpdateTeamMembers } from "hooks/api/useUpdateTeamMembers";
import { useGetUserById } from "hooks/api/user/useGetUserById";
import { useGetClients } from "hooks/api/useGetClients";
import TeamMemberDetail from "./TeamMemberDetail";
import EditTeamMember from "./EditTeamMember";
import { useUpdateUserEmail } from "hooks/api/useUpdateUserEmail";
import { GET_TEAM_MEMBERS, GET_USER_BY_ID } from "@constants/reactQueryKeys";

interface CollapsibleMemberViewProps {
  currentMember: any;
  mode: "edit" | "view";
  setOpen: (open: boolean) => void;
  userPermissions: any;
  refetchTeamMembers: () => void;
  setEditMode: (editMode: boolean) => void;
  setModePanel: (mode: "edit" | "view") => void;
  showEditButton: () => void;
  teamRoles?: any[];
  teamId: string | number;
  allTeamMembers?: any[];
}

export default function CollapsibleMemberView({
  currentMember,
  mode,
  setOpen,
  userPermissions,
  setEditMode,
  setModePanel,
  showEditButton,
  teamRoles = [],
  teamId,
  allTeamMembers = [],
}: CollapsibleMemberViewProps) {
  const { userId } = currentMember;
  const isEditMode = mode === "edit";
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  const { user, userLoading } = useGetUserById(userId);

  const isDesignatedCaregiver = currentMember.documentSigner;
  const { clients, clientsLoading } = useGetClients(
    isDesignatedCaregiver && { userIds: userId, role: "document_signer" }
  );

  const { mutateAsync: updateTeamUserAsync } = useUpdateTeamUser();

  const { mutateAsync: updateTeamMembersAsync } = useUpdateTeamMembers();

  const { updateUserEmailMutationAsync } = useUpdateUserEmail();

  const handleUpdateMember = async (data: any) => {
    if (!user) {
      showToast({ message: "User data not available", errorState: true });
      return;
    }

    const {
      email: newEmail,
      relationshipType,
      isLegalGuardian,
      isDesignatedCaregiver,
      ...userData
    } = data;

    const originalEmail = user.email;
    let hasError = false;

    if (newEmail && newEmail !== originalEmail) {
      try {
        await updateUserEmailMutationAsync({ userId, newEmail });
      } catch (error) {
        console.error("Error updating email:", error);
        showToast({ message: "Failed to update email", errorState: true });
        hasError = true;
      }
    }

    if (Object.keys(userData).length > 0) {
      try {
        await updateTeamUserAsync({ userId, data: userData });
      } catch (error) {
        console.error("Error updating user data:", error);
        showToast({ message: "Failed to update user information", errorState: true });
        hasError = true;
      }
    }

    if (relationshipType !== undefined || isLegalGuardian !== undefined || isDesignatedCaregiver !== undefined) {
      const updatedMember = {
        id: currentMember.id,
        roleId: relationshipType ?? currentMember.roleId,
        legalGuardian: isLegalGuardian ?? currentMember.legalGuardian,
        documentSigner: isDesignatedCaregiver ?? currentMember.documentSigner,
      };

      const allAcceptedMembers = allTeamMembers
        .filter(member => member.hasAcceptedInvite)
        .map(member => ({
          id: member.id,
          roleId: member.roleId,
          legalGuardian: member.legalGuardian,
          documentSigner: member.documentSigner,
        }));

      const teamMembersPayload = allAcceptedMembers.map(member => {
        if (member.id === currentMember.id) {
          return updatedMember;
        }

        if (updatedMember.documentSigner === true && member.documentSigner === true) {
          return {
            ...member,
            documentSigner: false,
          };
        }

        return member;
      });

      try {
        await updateTeamMembersAsync({
          teamId,
          teamMembers: teamMembersPayload
        });
      } catch (error) {
        console.error("Error updating team member roles:", error);
        showToast({ message: "Failed to update team member roles", errorState: true });
        hasError = true;
      }
    }

    if (!hasError) {
      showToast({ message: "Member updated successfully" });
      setOpen(false);
      queryClient.invalidateQueries([GET_TEAM_MEMBERS, { teamIds: teamId }]);
      queryClient.invalidateQueries([GET_USER_BY_ID, userId]);
    }
  };

  let researchParticipants = null;

  if (isDesignatedCaregiver && clients) {
    researchParticipants = clients?.map(
      ({ id, agree_to_research_participation, name, child_last_name }: any) => ({
        id,
        fullname: `${name} ${child_last_name}`,
        agreeToResearchParticipation: agree_to_research_participation,
      })
    );
  }

  return (
    <Box sx={{ padding: 7, width: "100%" }}>
      {userLoading || clientsLoading || userLoading ? (
        <ANLoadingScreen />
      ) : isEditMode ? (
        <EditTeamMember
          member={{ ...currentMember, ...user }}
          onClosePanel={() => setOpen(false)}
          onSubmit={handleUpdateMember}
          userPermissions={userPermissions}
          setEditMode={setEditMode}
          setModePanel={setModePanel}
          showEditButton={showEditButton}
          teamRoles={teamRoles}
          allTeamMembers={allTeamMembers}
        />
      ) : (
        <TeamMemberDetail
          member={{ ...currentMember, ...user }}
          researchParticipants={researchParticipants}
          onClosePanel={() => setOpen(false)}
          teamRoles={teamRoles}
        />
      )}
    </Box>
  );
}
